// Optimizer (SameGameParlay.jsx) in Floodlight (v1.71): the original black
// ground, glass panels with hairline dividers, purple/blue accents, Floodlight
// type. Positive status stays green (FL.pos).
import { StyleSheet, Platform } from 'react-native';
import { FL, FONT, label, body, nums, glass, ACCENT_GRADIENT } from '../components/floodlight';

const WEB = Platform.OS === 'web';
const ACCENT_FILL = WEB ? { backgroundImage: ACCENT_GRADIENT } : null;
const FIELD = { backgroundColor: FL.paper2, borderWidth: 1, borderColor: FL.hair2, borderRadius: 14 };

export const styles = StyleSheet.create({
    wrap: { alignSelf: 'center', width: '100%', maxWidth: 1480 },

    // Hero
    hero: { paddingTop: 40, paddingBottom: 32, flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', gap: 32 },
    heroStacked: { flexDirection: 'column', alignItems: 'flex-start', gap: 24, paddingTop: 28, paddingBottom: 24 },
    heroText: { flex: 1, minWidth: 0 },
    lede: { ...body(17), marginTop: 22, maxWidth: 560 },
    heroSide: { alignItems: 'flex-end', gap: 14 },
    heroSideStacked: { alignItems: 'flex-start', alignSelf: 'stretch' },

    // Mode switch: a glass pill, the selected side on the accent fill
    modes: { ...glass({ radius: 999 }), flexDirection: 'row', padding: 4, gap: 4 },
    modesStacked: { alignSelf: 'stretch' },
    mode: { minHeight: 44, paddingHorizontal: 20, borderRadius: 999, backgroundColor: 'transparent', borderWidth: 1, borderColor: 'transparent', alignItems: 'center', justifyContent: 'center' },
    modeStacked: { flex: 1, paddingHorizontal: 10 },
    modeOn: { backgroundColor: FL.green, ...ACCENT_FILL },
    modeDivider: {},
    modeText: { fontFamily: FONT, fontSize: 13, fontWeight: '700', letterSpacing: 1.1, textTransform: 'uppercase', color: FL.ink2 },
    modeTextOn: { color: FL.onGreen },

    status: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    statusDot: { width: 8, height: 8, borderRadius: 999, backgroundColor: FL.pos },
    statusText: { ...label(11) },

    // Form: one glass panel, two columns split by a hairline
    grid: { ...glass({ radius: 22 }), flexDirection: 'row', paddingHorizontal: 28 },
    gridStacked: { flexDirection: 'column', paddingHorizontal: 20 },
    col: { flex: 1, paddingVertical: 28, gap: 18, minWidth: 0 },
    colLeft: { paddingRight: 28 },
    colRight: { paddingLeft: 28, borderLeftWidth: 1, borderLeftColor: FL.hair },
    colStackedTop: { paddingRight: 0, paddingVertical: 22 },
    colStackedBottom: { paddingLeft: 0, paddingVertical: 22, borderLeftWidth: 0, borderTopWidth: 1, borderTopColor: FL.hair },
    colHead: { flexDirection: 'row', alignItems: 'baseline', gap: 12, marginBottom: 4 },
    colIndex: { fontFamily: FONT, fontSize: 22, fontWeight: '800', color: FL.greenInk, letterSpacing: -0.5, ...(WEB ? { fontStretch: '104%' } : {}) },
    colTitle: { ...label(12), color: FL.ink, letterSpacing: 1.8 },

    lineRow: { flexDirection: 'row', gap: 14 },
    lineCell: { flex: 1, minWidth: 0 },
    fieldLabel: { ...label(11), marginBottom: 8 },
    textField: { ...FIELD },
    textInput: {
        minHeight: 48, paddingVertical: 13, paddingHorizontal: 16, fontFamily: FONT, fontSize: 15, fontWeight: '500', color: FL.ink, ...nums,
        ...(WEB ? { outlineStyle: 'none' } : {}),
    },
    hint: { ...body(13), color: FL.red, marginTop: 8 },

    // Books: a second glass panel
    books: { ...glass({ radius: 22 }), marginTop: 16, paddingVertical: 24, paddingHorizontal: 28, flexDirection: 'row', gap: 28 },
    booksStacked: { flexDirection: 'column', gap: 20, paddingHorizontal: 20 },
    boost: { width: 220, opacity: 0.5 },
    boostStacked: { width: '100%' },
    betaTag: { borderWidth: 1, borderColor: FL.hair2, borderRadius: 999, paddingHorizontal: 8, paddingVertical: 1 },
    betaText: { ...label(10), color: FL.ink2 },
    bookPicker: { flex: 1, minWidth: 0 },
    selector: { ...FIELD, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, minHeight: 48, paddingVertical: 13, paddingHorizontal: 16 },
    selectorText: { fontFamily: FONT, fontSize: 15, fontWeight: '500', color: FL.ink, flex: 1 },
    panel: { ...glass({ strong: true, radius: 18 }), marginTop: 10, padding: 18, gap: 14 },
    panelActions: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
    tierHead: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 6 },
    tierRule: { width: 20, height: 1, backgroundColor: FL.green, ...ACCENT_FILL },
    tierLabel: { ...label(11), color: FL.ink2, letterSpacing: 1.6 },
    chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
    chip: { minHeight: 36, justifyContent: 'center', paddingHorizontal: 14, borderRadius: 999, borderWidth: 1, borderColor: FL.hair2, backgroundColor: FL.card },
    chipOn: { backgroundColor: FL.green, borderColor: 'transparent', ...ACCENT_FILL },
    chipText: { fontFamily: FONT, fontSize: 13, fontWeight: '600', color: FL.ink },
    chipTextOn: { fontWeight: '700', color: FL.onGreen },
    picked: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 12 },

    // Added queries / legs: numbered rows
    list: { paddingTop: 32 },
    listHead: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 },
    row: { flexDirection: 'row', alignItems: 'center', gap: 18, paddingVertical: 14, paddingHorizontal: 12, borderTopWidth: 1, borderTopColor: FL.hair },
    rowLast: { borderBottomWidth: 1, borderBottomColor: FL.hair },
    rowNum: { ...label(12), color: FL.greenInk, width: 52, letterSpacing: 1.4, ...nums },
    rowBody: { flex: 1, minWidth: 0, gap: 4 },
    rowText: { fontFamily: FONT, fontSize: 16, fontWeight: '700', color: FL.ink, letterSpacing: -0.2 },
    rowMeta: { ...label(11) },
    remove: { width: 40, height: 40, borderRadius: 999, borderWidth: 1, borderColor: FL.hair2, backgroundColor: FL.card, alignItems: 'center', justifyContent: 'center' },
    notice: { ...body(14), color: FL.amber, backgroundColor: FL.amberWash, borderRadius: 12, paddingVertical: 10, paddingHorizontal: 14, marginTop: 16 },

    // Your search: the lit glass stage
    stage: {
        ...glass({ strong: true, radius: 22 }),
        marginTop: 24, paddingVertical: 24, paddingHorizontal: 26, gap: 12, borderColor: 'rgba(162,89,255,0.35)',
        ...(WEB ? { backgroundImage: 'radial-gradient(ellipse 60% 140% at 0% 50%, rgba(124,58,237,0.20) 0%, transparent 70%)' } : {}),
    },
    stageHead: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    stageDot: { width: 8, height: 8, borderRadius: 999, backgroundColor: FL.greenInk, ...(WEB ? { boxShadow: '0 0 12px rgba(196,161,255,0.8)' } : {}) },
    stageLabel: { ...label(12), color: FL.stageSoft, letterSpacing: 1.8 },
    stageText: { fontFamily: FONT, fontSize: 21, lineHeight: 29, fontWeight: '700', color: FL.onStage, letterSpacing: -0.3, ...nums },
    stageEmpty: { color: FL.stageFaint, fontWeight: '500' },

    error: { marginTop: 16, borderRadius: 14, borderWidth: 1, borderColor: 'rgba(255,106,92,0.45)', backgroundColor: FL.redWash, paddingVertical: 12, paddingHorizontal: 16 },
    errorText: { ...body(15), color: FL.red, fontWeight: '600' },

    actions: { marginTop: 24, flexDirection: 'row', flexWrap: 'wrap', gap: 12, alignItems: 'stretch' },
    actionsStacked: { flexDirection: 'column' },
    primary: { minWidth: 260, marginLeft: 'auto' },
    primaryStacked: { marginLeft: 0, minWidth: 0 },

    // Coming soon
    soonBackdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.72)', alignItems: 'center', justifyContent: 'center', padding: 16, ...(WEB ? { backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' } : {}) },
    soonCard: {
        ...glass({ strong: true, radius: 24 }),
        width: '100%', maxWidth: 520, backgroundColor: FL.paper, borderColor: FL.hair2, padding: 32, gap: 20,
        ...(WEB ? { backgroundImage: 'radial-gradient(ellipse 80% 70% at 0% 0%, rgba(124,58,237,0.22) 0%, transparent 70%), linear-gradient(rgba(255,255,255,0.07), rgba(255,255,255,0.07))', boxShadow: '0 30px 80px rgba(0,0,0,0.6)' } : {}),
    },
    soonBody: { ...body(16) },
});
